import {
  booleanPointInPolygon,
  distance,
  featureCollection,
  featureEach,
  getType,
  lineString,
} from '@turf/turf';
import { keyBy } from 'lodash';
import { Feature, LineString, Point, Polygon } from 'geojson';
import { SHARED_GENERATE_INPUTS } from './reversePointsAndRows';
import {
  DEFAULT_BETWEEN_ROW_SPACING,
  DEFAULT_IN_ROW_SPACING,
  geometryTypes,
  NONE,
  UNKNOWN_CROP,
} from '../../constants/fields';
import { augmentFieldFeature } from '../layerAugmenters';
import { feetPerMile } from '../../constants/units';
import { cropNameForRow, patternIdForRow, rowHasPattern } from './rows';
import generateRowPoints from './generateRowPoints';
import generateGridPoints from './generateGridPoints';
import {
  DataMaps,
  GridProps,
  HeadlandProps,
  PointsAndRowsProps,
} from '../../types/pointsAndRows';
import { unitsMiles } from '../units';
import { getGuidelines } from './getGuidelines';

interface GeneratePolygonPointsAndRowsProps
  extends GridProps,
    HeadlandProps,
    PointsAndRowsProps {
  fieldFeature: Feature<Polygon>;
}

export const GENERATE_POLY_INPUTS = [
  'fieldFeature',
  'betweenRowSpacing',
  'inRowSpacing',
  'headlandWidth',
  'headlandInnerFeature',
  'rotation',
  'offsetX',
  'offsetY',
  'crop',
  'primaryInRowPattern',
  'rowOverrides',
  'type',
  ...SHARED_GENERATE_INPUTS,
];

/**
 * Generates points and rows within a polygon using betweenRowSpacing and
 * inRowSpacing (in ft). For any patterned or override rows, generates points
 * along lineStrings from the first grid point to the polygon boundary.
 * Rotation:
 *   - By default, with 0 rotation, rows are vertical (North to South).
 *   - Rotation is specified in degrees, clockwise.
 */
const generatePolygonPointsAndRows = (
  // Update GENERATE_POLY_INPUTS above if these params change
  {
    fieldFeature,
    betweenRowSpacing = DEFAULT_BETWEEN_ROW_SPACING,
    inRowSpacing = DEFAULT_IN_ROW_SPACING,
    headlandInnerFeature = false,
    headlandWidth = 0,
    rotation,
    offsetX,
    offsetY,
    crop,
    primaryInRowPattern,
    rowOverrides = [],
    type,
  }: GeneratePolygonPointsAndRowsProps,
  dataMaps: DataMaps,
) => {
  // Input validation
  if (!fieldFeature) return {};
  if (!betweenRowSpacing) throw new Error('Requires betweenRowSpacing');
  if (!inRowSpacing && !primaryInRowPattern)
    throw new Error('Requires inRowSpacing or primaryInRowPattern');
  if (getType(fieldFeature) !== geometryTypes.POLYGON) {
    throw new Error('fieldFeature must be a Polygon');
  }
  // Use inner feature of the augmented fieldFeature
  const [innerPolygon] =
    augmentFieldFeature({
      fieldFeature,
      headlandInnerFeature,
      headlandWidth,
      type,
    })?.features || [];

  // Generate point grid
  const { points, rotationCenter } = generateGridPoints(
    {
      betweenRowSpacing,
      fieldFeature: innerPolygon as Feature<Polygon>,
      inRowSpacing,
      offsetX,
      offsetY,
      primaryInRowPattern,
      rotation,
    },
    dataMaps,
  );

  const cropsByName = keyBy(Object.values(dataMaps.cropMap), 'name');
  const sectionPattern =
    primaryInRowPattern && dataMaps.inRowPatternsMap[primaryInRowPattern.id];

  // Filter points and generate rows
  const filteredPoints = <Feature<Point>[]>[];
  const rowSegments = <Feature<LineString>[]>[];
  let rowPoints = <Feature<Point>[]>[];
  // Debug: added points/rows are shown on the map in red
  const debugPoints = <Feature<Point>[]>[];
  const debugRows = <Feature<LineString>[]>[];

  featureEach(points, (pt) => {
    // Grid point inside polygon along row
    if (booleanPointInPolygon(pt, innerPolygon)) {
      // Add point to row
      rowPoints.push({
        ...pt,
        properties: {
          ...pt.properties,
          cropName: crop?.name,
        },
      });
      // First grid point outside of polygon at the end of the row
    } else if (rowPoints.length) {
      const startPoint = rowPoints[0];
      const { guidelines, partitionedRowPoints } = getGuidelines({
        rowPoints,
        startPoint,
        endPointExcl: pt,
        polygon: innerPolygon,
      });
      guidelines.forEach((guideline, i) => {
        // Get grid points on this guideline
        rowPoints = partitionedRowPoints[i];
        const override = rowOverrides[rowSegments.length];
        const {
          cropName: overrideCropName = crop?.name,
          inRowSpacing: overrideSpacing,
        } = override || {};
        // Generate row points for override/pattern, unless NONE row
        if ((override || sectionPattern) && overrideCropName !== NONE) {
          const overrideCrop = overrideCropName
            ? cropsByName[overrideCropName] || { name: overrideCropName }
            : UNKNOWN_CROP;
          const hasPattern = rowHasPattern(override, {
            primaryInRowPattern,
          });
          const overridePattern =
            dataMaps.inRowPatternsMap[
              patternIdForRow(override, {
                primaryInRowPattern,
              })
            ];
          rowPoints =
            generateRowPoints(
              {
                rows: featureCollection([guideline]),
                inRowSpacing: overrideSpacing || inRowSpacing,
                crop: hasPattern ? undefined : overrideCrop,
                primaryInRowPattern: overridePattern,
              },
              dataMaps,
            ).points?.features || [];
        }
        // Add all row points to the filtered points, unless NONE row
        const isNoneRow = !!override && overrideCropName === NONE;
        if (!isNoneRow) {
          rowPoints.forEach((rowPoint) => {
            filteredPoints.push({
              ...rowPoint,
              properties: {
                ...rowPoint.properties,
                row: rowSegments.length,
              },
            });
          });
        }
        // Add row to the row segments
        const firstCoords = guideline.geometry.coordinates[0];
        const lastCoords = rowPoints[rowPoints.length - 1].geometry.coordinates;
        const row = lineString([firstCoords, lastCoords], {
          row: rowSegments.length,
          treeCount: isNoneRow ? 0 : rowPoints.length,
          rowLength:
            distance(firstCoords, lastCoords, unitsMiles) * feetPerMile,
          cropName: cropNameForRow(override, {
            crop,
            primaryInRowPattern,
          }),
        });
        rowSegments.push(row);
      });
      rowPoints = []; // Clear rowPoints to start next row
    }
  });

  return {
    points: featureCollection(filteredPoints),
    rows: featureCollection(rowSegments),
    rotationCenter,
    // Once we've regenerated a grid section's points, any legacy offset will no longer exist
    legacyGridOffset: false,
    // Debugging
    allPoints: points,
    debugPoints: featureCollection(debugPoints),
    debugRows: featureCollection(debugRows),
  };
};

export default generatePolygonPointsAndRows;
